import type { MultiOutcomeState } from "./markets";

export interface MoynPositionSummary {
  participants: number;
  positionsByOutcome: Record<number, { sharesYes: number; sharesNo: number }>;
  netInvested: number;
}

/** Gross collateral (micro-units) from chain position row yes/no cost basis fields. */
export const grossCostBasisMicro = (row: {
  yes_cost_basis?: number;
  no_cost_basis?: number;
}): number => (row.yes_cost_basis ?? 0) + (row.no_cost_basis ?? 0);

/** Sum (yes_cost_basis + no_cost_basis) per outcome_id across all moynpos rows (micro-units). */
export const aggregateMoynVolumeByOutcomeMicro = (
  rows: any[],
): Record<number, number> => {
  const byOutcome: Record<number, number> = {};
  for (const row of rows) {
    const raw = row?.data ?? row;
    const oid = Number(raw?.outcome_id);
    if (!Number.isFinite(oid)) continue;
    const micro = grossCostBasisMicro(raw);
    byOutcome[oid] = (byOutcome[oid] ?? 0) + micro;
  }
  return byOutcome;
};

/**
 * Binary LMSR (`poslmsr`): one row per trader with both sides. For market-detail
 * outcome rows, show summed `yes_cost_basis` on outcome 0 (Yes) and summed
 * `no_cost_basis` on outcome 1 (No). Values are micro-units.
 */
export const aggregatePoslmsrBinaryDisplayVolumeMicro = (
  rows: any[],
): Record<number, number> => {
  let yesSum = 0;
  let noSum = 0;
  for (const row of rows) {
    const raw = row?.data ?? row;
    yesSum += raw?.yes_cost_basis ?? 0;
    noSum += raw?.no_cost_basis ?? 0;
  }
  return { 0: yesSum, 1: noSum };
};

export const computeNetInvested = (collateralSpentMicro: number): number => {
  if (!collateralSpentMicro || collateralSpentMicro <= 0) {
    return 0;
  }

  const denominator = 10_000 + 0;
  if (denominator <= 0) {
    return 0;
  }

  const netMicro = Math.floor((collateralSpentMicro * 10_000) / denominator);
  return Math.floor(netMicro / 1_000_000);
};

export const normalizeMoynStateRows = (rows: any[]): MultiOutcomeState[] =>
  rows.map((row: any) => {
    const source = row?.data ?? row;
    return {
      outcome_id: source.outcome_id ?? 0,
      q: source.q ?? source.q_yes ?? 0,
      q_yes: source.q_yes ?? source.q ?? 0,
      q_no: source.q_no ?? 0,
      volume: source.volume ?? 0,
    };
  });

export const summarizeMoynPositions = (
  rows: any[],
  userAccount: string | null,
  feeBps: number,
): MoynPositionSummary => {
  const participants = new Set(rows.map((row: any) => row.user).filter(Boolean))
    .size;
    
  if (!userAccount) {
    return {
      participants,
      positionsByOutcome: {},
      netInvested: 0,
    };
  }

  const userRows = rows.filter((row: any) => row.user === userAccount);
  const grossSpent = userRows.reduce(
    (sum: number, row: any) => sum + grossCostBasisMicro(row),
    0,
  );

  const positionsByOutcome = Object.fromEntries(
    userRows.map((row: any) => [
      row.outcome_id,
      {
        sharesYes: (row.shares_yes || 0) / 1_000_000,
        sharesNo: (row.shares_no || 0) / 1_000_000,
      },
    ]),
  );

  return {
    participants,
    positionsByOutcome,
    netInvested: computeNetInvested(grossSpent),
  };
};
